import React, { Fragment } from "react";

import clsx from "clsx";

import { ControlBar } from "@components/control-bar";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  CompareDateRangeEnum,
  DateRangeChoiceEnum,
  TimeGranularity_Deprecated,
  type DateRangeChoiceV2,
  type FixedDateInterval,
  type ICompareDateRangeEnum,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularity,
} from "@north-beam/nb-common";
import {
  AttributionWindowSelect,
  useAttributionWindowOptions,
} from "@pages/sales/report-body-control/attribution-window-select";
import { NbAccountingModeSelect } from "@pages/sales/report-body-control/nb-accounting-mode-select";
import { formatDateRangeText } from "@shared/date-range-picker";
import {
  AttributionModelSelect,
  CompareDateRangeSelect,
  DateRangeSelect,
  TimeGranularitySelect,
} from "@shared/selects";
import { renderChevronAnimation } from "@shared/selects/utils";

import { type ReportState } from "./report-state";

export interface ControlCenterProps {
  onUpdate: (state: ReportState) => void;
  isLoading: boolean;
  reportState: ReportState;
}

export function ControlCenter(props: ControlCenterProps) {
  const { reportState, isLoading, onUpdate } = props;

  const {
    dateRange,
    compareDateRange,
    attributionModel,
    nbAccountingMode,
    timeGranularity,
    attributionWindow: selectedAttributionWindow,
  } = reportState;

  const { options: windowOptions, reconciledAttributionWindow } =
    useAttributionWindowOptions({
      timeGranularity,
      selectedAttributionWindow,
    });

  const update = React.useCallback(
    (action: ReportAction) => onUpdate(ReportStateReducer(reportState, action)),
    [onUpdate, reportState],
  );

  const maxLookbackDays = 20 * 365;
  const attributionWindowChoices = windowOptions;

  let attributionWindowTitle: string | React.ReactElement =
    "ATTRIBUTION WINDOW";
  if (
    !attributionWindowChoices.find(
      (v) => v.value === reconciledAttributionWindow,
    )
  ) {
    attributionWindowTitle = (
      <>
        <span>Attribution window</span>
        &nbsp;-&nbsp;
        <span className="text-danger">
          Unsupported window; please pick another.
        </span>
      </>
    );
  }

  const dateRangeCurrent =
    typeof dateRange === "string"
      ? DateRangeChoiceEnum.toReactSelect(dateRange)
      : dateRange;

  const compareDateRangeCurrent =
    typeof compareDateRange === "string"
      ? CompareDateRangeEnum.toReactSelect(compareDateRange)
      : compareDateRange;

  return (
    <ControlBar>
      <div
        className="flex items-center max-w-5xl space-x-2"
        style={{ flex: "3 3 auto" }}
      >
        <AttributionModelSelect
          isLoading={isLoading}
          attributionModel={attributionModel}
          onChange={(value) =>
            update({ type: "UpdateAttributionMethod", value })
          }
        />
        <AttributionWindowSelect
          label={attributionWindowTitle}
          isLoading={isLoading}
          attributionModel={attributionModel}
          attributionWindow={reconciledAttributionWindow}
          timeGranularity={timeGranularity}
          onChange={(value) =>
            update({ type: "UpdateAttributionWindow", value })
          }
        />
        <NbAccountingModeSelect
          isLoading={isLoading}
          nbAccountingMode={nbAccountingMode}
          onChange={(value) => update({ type: "UpdateAccountingMode", value })}
        />
      </div>
      <div className="flex flex-row items-center justify-content-end pl-5">
        <Popover className="nb-input-container relative">
          <PopoverButton
            className="nb-input flex flex-col items-start mr-2 text-sm w-56 h-14"
            disabled={isLoading}
          >
            {({ open }) => (
              <div className="flex items-center space-between w-full">
                <div className="flex-grow text-left">
                  <div>
                    {formatDateRangeText(dateRangeCurrent)},{" "}
                    {TimeGranularity_Deprecated.getLabel(timeGranularity)}
                  </div>
                  <small className="text-muted mt-1 text-small">
                    Compared to {formatDateRangeText(compareDateRangeCurrent)}
                  </small>
                </div>
                {renderChevronAnimation(open)}
              </div>
            )}
          </PopoverButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel
              className={clsx(
                "absolute right-0 z-100 mt-2 w-72 elevation-1",
                "rounded-md p-3 bg-white flex flex-col space-y-2",
              )}
            >
              <TimeGranularitySelect
                isLoading={isLoading}
                timeGranularity={timeGranularity}
                onChange={(value) =>
                  update({ type: "UpdateTimeGranularity", value })
                }
              />
              <DateRangeSelect
                isLoading={isLoading}
                maxLookbackDays={maxLookbackDays}
                dateRange={dateRange}
                onUpdate={(value) => update({ type: "UpdateDateRange", value })}
              />
              <CompareDateRangeSelect
                isLoading={isLoading}
                dateRange={dateRange}
                compareDateRange={compareDateRange}
                onUpdate={(value) =>
                  update({ type: "UpdateCompareDateRange", value })
                }
              />
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
    </ControlBar>
  );
}

interface UpdateDateRange {
  type: "UpdateDateRange";
  value: DateRangeChoiceV2 | FixedDateInterval;
}

interface UpdateCompareDateRange {
  type: "UpdateCompareDateRange";
  value: ICompareDateRangeEnum | FixedDateInterval;
}

interface UpdateAttributionMethod {
  type: "UpdateAttributionMethod";
  value: any;
}

interface UpdateTimeGranularity {
  type: "UpdateTimeGranularity";
  value: ITimeGranularity;
}

interface UpdateAttributionWindow {
  type: "UpdateAttributionWindow";
  value: IMCAttributionWindowDays;
}

interface UpdateAccountingMode {
  type: "UpdateAccountingMode";
  value: INBAccountingMode;
}

export type ReportAction =
  | UpdateDateRange
  | UpdateCompareDateRange
  | UpdateTimeGranularity
  | UpdateAttributionMethod
  | UpdateAttributionWindow
  | UpdateAccountingMode;

export function ReportStateReducer(
  state: ReportState,
  action: ReportAction,
): ReportState {
  switch (action.type) {
    case "UpdateDateRange":
      return { ...state, dateRange: action.value };
    case "UpdateCompareDateRange":
      return { ...state, compareDateRange: action.value };
    case "UpdateTimeGranularity":
      return {
        ...state,
        timeGranularity: action.value,
        dateRange: action.value !== "hourly" ? "last28Days" : "last3Days",
        compareDateRange: "lastPeriod",
      };
    case "UpdateAttributionMethod":
      return { ...state, attributionModel: action.value };
    case "UpdateAttributionWindow":
      return { ...state, attributionWindow: action.value };
    case "UpdateAccountingMode":
      return { ...state, nbAccountingMode: action.value };
  }
}
